import { Step } from "@/backend/models/containers/step";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useParameterStore } from "../store/ParameterStore";
import { CollapseButton } from "./buttons/CollapseButton";
import { Form } from "react-bootstrap";
import { GenericParameterSelect } from "./parameters/GenericParameterSelect";
import { Scaffoldable, useBuildCurrentParameterResponse, useGetParametersToAssignFromInput } from "../hooks/parameters";
import { ContainerVisibilityButton } from "./buttons/ContainerVisibilityButton";
import { Container } from "@/backend/models/containers/container";
import { ParameterSubjectType, ParameterType } from "@/types/parameterTypes";

export function Template<T extends Scaffoldable>(props: { initItem: T, updateItem: (item: T) => Promise<any> }) {
    const { initItem, updateItem } = props;
    const advancedValueExists = !!initItem.rangeParameterId || !!(initItem instanceof Step && initItem.conditionalFieldId) || !!initItem.skipParameterId;
    // Always start collapsed. A marker on the bar keeps configured values
    // discoverable without the section expanding itself on every render.
    const [showAdvanced, setShowAdvanced] = useState(false);

    return (
      <div className="border rounded bg-light mt-1">
        <div className='d-flex align-items-center advanced-view-bar'>
          <CollapseButton showElement={showAdvanced} setShowElement={setShowAdvanced} title={I18n.get('advancedView')} size='sm' />
          <span className="fw-semibold text-muted small">{I18n.get('advancedView')}</span>
          { advancedValueExists && !showAdvanced &&
            <span className='advanced-view-marker ms-2' title={I18n.get('advancedValuesSet')} aria-label={I18n.get('advancedValuesSet')} />
          }
        </div>
        {showAdvanced && (<div className='px-2 pb-2'>
          <div className='d-flex w-100 gap-3 pb-2'>
            <RangeParameterSelector item={initItem} updateItem={updateItem} />
            <SkipParameterSelector item={initItem} updateItem={updateItem} />
          </div>
          {initItem instanceof Container && (<ContainerVisibilityButton item={initItem} />)}
        </div>
        )}
      </div>
    );
}

export function SkipParameterSelector<T extends Scaffoldable>(props: { item: T, updateItem: (item: T) => Promise<any> }) {
  const { item, updateItem } = props;
  const parameters = useGetParametersToAssignFromInput(item, item.id).filter(param => param.fieldType === CUSTOM_FIELD_TYPES.Boolean && param.type === ParameterType.CONFIG);
  const currentParameter = useParameterStore(useShallow(state => state.parameters.find(p => p.id === item.skipParameterId)));

  const currentParameterObj = useBuildCurrentParameterResponse(currentParameter, item.skipKeyId);

  return (
    <div className='flex-fill'>
      <Form.Label className="col-form-label col-form-label-sm">{I18n.get('skip')}</Form.Label>
      <GenericParameterSelect
        parameters={parameters}
        currentParameter={currentParameterObj}
        createReference={(param) =>  void updateItem({ ...item, skipParameterId: param.id, skipKeyId: param.keyId || '' })}
        deleteReference={() => void updateItem({ ...item, skipParameterId: '', skipKeyId: '' })}
      />
    </div>
  );
}

function RangeParameterSelector<T extends Scaffoldable>(props: { item: T, updateItem: (item: T) => Promise<any> }) {
  const { item, updateItem } = props;
  const parameters = useParameterStore(useShallow(state => state.parameters.filter(p => p.hasMultipleValues() && p.subjectType === ParameterSubjectType.PROCEDURE && p.type === ParameterType.CONFIG)));
  const currentParameter = useParameterStore(useShallow(state => state.parameters.find(p => p.id === item.rangeParameterId)));

  return (
    <div className='flex-fill'>
      <Form.Label className="col-form-label col-form-label-sm">{I18n.get('range')}</Form.Label>
      <GenericParameterSelect
        parameters={parameters}
        currentParameter={currentParameter}
        createReference={(param) => void updateItem({ ...item, rangeParameterId: param.id })}
        deleteReference={() => void updateItem({ ...item, rangeParameterId: '' })}
      />
    </div>
  );
}

